// src/components/users/UserList.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import {
  fetchUsers,
  deleteUser,
  toggleUserStatus,
  resetUserPassword,
  verifyUser,
  clearUserError,
  clearUserSuccess,
} from '../../store/actions/userActions';
import UserFilters from './UserFilters';
import Pagination from '../common/Pagination';
import SearchBar from '../common/SearchBar';
import LoadingSpinner from '../common/LoadingSpinner';
import Alert from '../common/Alert';
import ConfirmDialog from '../common/ConfirmDialog';

const UserList = () => {
  const dispatch = useDispatch();
  const { users, loading, error, success, pagination } = useSelector(
    (state) => state.users
  );

  const [filters, setFilters] = useState({
    page: 1,
    limit: 20,
    search: '',
    department: '',
    team: '',
    employmentStatus: '',
    employeeType: '',
    isActive: '',
  });

  const [selectedUser, setSelectedUser] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showStatusConfirm, setShowStatusConfirm] = useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);
  const [showVerifyConfirm, setShowVerifyConfirm] = useState(false);
  const [temporaryPassword, setTemporaryPassword] = useState(null);

  // Use refs to track initial mount and filter changes
  const initialMount = useRef(true);
  const filtersRef = useRef(filters);

  // Styles object
  const styles = {
    container: {
      maxWidth: '1280px',
      margin: '0 auto',
      padding: '2rem 1rem',
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '1.5rem',
    },
    title: {
      fontSize: '1.5rem',
      fontWeight: 700,
      color: '#111827',
      margin: 0,
    },
    createButton: {
      padding: '0.5rem 1rem',
      backgroundColor: '#2563eb',
      color: '#ffffff',
      border: 'none',
      borderRadius: '0.375rem',
      fontSize: '0.875rem',
      fontWeight: 500,
      cursor: 'pointer',
      textDecoration: 'none',
      display: 'inline-flex',
      alignItems: 'center',
      transition: 'background-color 0.2s',
    },
    createButtonHover: {
      backgroundColor: '#1d4ed8',
    },
    searchContainer: {
      marginBottom: '1rem',
    },
    filtersContainer: {
      marginBottom: '1.5rem',
    },
    tableContainer: {
      backgroundColor: '#ffffff',
      borderRadius: '0.5rem',
      boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
      overflow: 'hidden',
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
    },
    tableHeader: {
      backgroundColor: '#f9fafb',
      borderBottom: '1px solid #e5e7eb',
    },
    tableHeaderCell: {
      padding: '0.75rem 1.5rem',
      textAlign: 'left',
      fontSize: '0.75rem',
      fontWeight: 500,
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
      color: '#6b7280',
      borderBottom: '1px solid #e5e7eb',
    },
    tableHeaderCellRight: {
      padding: '0.75rem 1.5rem',
      textAlign: 'right',
      fontSize: '0.75rem',
      fontWeight: 500,
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
      color: '#6b7280',
      borderBottom: '1px solid #e5e7eb',
    },
    tableRow: {
      borderBottom: '1px solid #e5e7eb',
      transition: 'background-color 0.2s',
    },
    tableRowHover: {
      backgroundColor: '#f9fafb',
    },
    tableCell: {
      padding: '1rem 1.5rem',
      fontSize: '0.875rem',
      color: '#111827',
    },
    tableCellRight: {
      padding: '1rem 1.5rem',
      fontSize: '0.875rem',
      color: '#111827',
      textAlign: 'right',
    },
    
    // User cell styles
    userContainer: {
      display: 'flex',
      alignItems: 'center',
    },
    avatar: {
      flexShrink: 0,
      width: '2.5rem',
      height: '2.5rem',
      borderRadius: '9999px',
      backgroundColor: '#d1d5db',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      overflow: 'hidden',
    },
    avatarImage: {
      width: '100%',
      height: '100%',
      objectFit: 'cover',
    },
    avatarFallback: {
      color: '#4b5563',
      fontWeight: 500,
      fontSize: '0.875rem',
    },
    userInfo: {
      marginLeft: '1rem',
    },
    userName: {
      fontSize: '0.875rem',
      fontWeight: 500,
      color: '#111827',
    },
    userEmail: {
      fontSize: '0.875rem',
      color: '#6b7280',
    },
    userEmployeeId: {
      fontSize: '0.75rem',
      color: '#9ca3af',
    },
    
    // Contact cell styles
    jobTitle: {
      fontSize: '0.875rem',
      color: '#111827',
    },
    phoneNumber: {
      fontSize: '0.875rem',
      color: '#6b7280',
    },
    
    // Department/Team styles
    department: {
      fontSize: '0.875rem',
      color: '#111827',
    },
    team: {
      fontSize: '0.875rem',
      color: '#6b7280',
    },
    
    // Status badge styles
    statusBadgeActive: {
      display: 'inline-flex',
      padding: '0.25rem 0.5rem',
      fontSize: '0.75rem',
      fontWeight: 500,
      backgroundColor: '#d1fae5',
      color: '#065f46',
      borderRadius: '9999px',
    },
    statusBadgeInactive: {
      display: 'inline-flex',
      padding: '0.25rem 0.5rem',
      fontSize: '0.75rem',
      fontWeight: 500,
      backgroundColor: '#fee2e2',
      color: '#991b1b',
      borderRadius: '9999px',
    },
    statusBadgeUnverified: {
      display: 'inline-flex',
      padding: '0.25rem 0.5rem',
      fontSize: '0.75rem',
      fontWeight: 500,
      backgroundColor: '#fef3c7',
      color: '#92400e',
      borderRadius: '9999px',
    },
    
    // Role badges container
    rolesContainer: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '0.25rem',
    },
    roleBadge: {
      padding: '0.25rem 0.5rem',
      fontSize: '0.75rem',
      fontWeight: 500,
      backgroundColor: '#dbeafe',
      color: '#1e40af',
      borderRadius: '0.25rem',
    },
    roleBadgeMore: {
      padding: '0.25rem 0.5rem',
      fontSize: '0.75rem',
      fontWeight: 500,
      backgroundColor: '#f3f4f6',
      color: '#374151',
      borderRadius: '0.25rem',
    },
    
    // Actions container
    actionsContainer: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-end',
      gap: '0.5rem',
    },
    actionButton: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '0.25rem',
      border: 'none',
      background: 'none',
      cursor: 'pointer',
      color: '#6b7280',
      transition: 'color 0.2s',
      borderRadius: '0.375rem',
    },
    actionButtonDisabled: {
      opacity: 0.5,
      cursor: 'not-allowed',
    },
    viewButton: {
      color: '#2563eb',
    },
    viewButtonHover: {
      color: '#1d4ed8',
    },
    editButton: {
      color: '#4f46e5',
    },
    editButtonHover: {
      color: '#4338ca',
    },
    verifyButton: {
      color: '#059669',
    },
    verifyButtonHover: {
      color: '#047857',
    },
    passwordButton: {
      color: '#d97706',
    },
    passwordButtonHover: {
      color: '#b45309',
    },
    statusButtonActive: {
      color: '#dc2626',
    },
    statusButtonInactive: {
      color: '#059669',
    },
    statusButtonHoverActive: {
      color: '#b91c1c',
    },
    statusButtonHoverInactive: {
      color: '#047857',
    },
    deleteButton: {
      color: '#dc2626',
    },
    deleteButtonHover: {
      color: '#b91c1c',
    },
    icon: {
      width: '1.25rem',
      height: '1.25rem',
    },
    
    // Empty state
    emptyState: {
      textAlign: 'center',
      padding: '3rem 1.5rem',
      color: '#6b7280',
      fontSize: '0.875rem',
    },
  };

  // Memoize fetch function
  const fetchUsersData = useCallback(() => {
    dispatch(fetchUsers(filters));
  }, [dispatch, filters]);

  // Only fetch when filters actually change
  useEffect(() => {
    if (initialMount.current) {
      initialMount.current = false;
      fetchUsersData();
    } else {
      if (JSON.stringify(filtersRef.current) !== JSON.stringify(filters)) {
        filtersRef.current = filters;
        fetchUsersData();
      }
    }
  }, [fetchUsersData, filters]);

  // Cleanup function
  useEffect(() => {
    return () => {
      dispatch(clearUserError());
      dispatch(clearUserSuccess());
    };
  }, [dispatch]);

  const handleFilterChange = useCallback((newFilters) => {
    setFilters((prev) => ({
      ...prev,
      ...newFilters,
      page: 1,
    }));
  }, []);

  const handleSearch = useCallback((searchTerm) => {
    setFilters((prev) => ({
      ...prev,
      search: searchTerm,
      page: 1,
    }));
  }, []);

  const handlePageChange = useCallback((page) => {
    setFilters((prev) => ({
      ...prev,
      page,
    }));
  }, []);

  const handleDeleteClick = useCallback((user) => {
    setSelectedUser(user);
    setShowDeleteConfirm(true);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    try {
      await dispatch(deleteUser(selectedUser.id));
      setShowDeleteConfirm(false);
      setSelectedUser(null);
      fetchUsersData();
    } catch (error) {
      console.error('Delete failed:', error);
    }
  }, [dispatch, selectedUser, fetchUsersData]);

  const handleStatusToggle = useCallback((user) => {
    setSelectedUser(user);
    setShowStatusConfirm(true);
  }, []);

  const handleStatusConfirm = useCallback(async () => {
    try {
      await dispatch(
        toggleUserStatus(selectedUser.id, !selectedUser.isActive)
      );
      setShowStatusConfirm(false);
      setSelectedUser(null);
      fetchUsersData();
    } catch (error) {
      console.error('Status toggle failed:', error);
    }
  }, [dispatch, selectedUser, fetchUsersData]);

  const handlePasswordReset = useCallback((user) => {
    setSelectedUser(user);
    setShowPasswordConfirm(true);
  }, []);

  const handlePasswordConfirm = useCallback(async () => {
    try {
      const result = await dispatch(resetUserPassword(selectedUser.id));
      setTemporaryPassword(result.temporaryPassword);
      setShowPasswordConfirm(false);
      setSelectedUser(null);
    } catch (error) {
      console.error('Password reset failed:', error);
    }
  }, [dispatch, selectedUser]);

  const handleVerify = useCallback((user) => {
    setSelectedUser(user);
    setShowVerifyConfirm(true);
  }, []);

  const handleVerifyConfirm = useCallback(async () => {
    try {
      await dispatch(verifyUser(selectedUser.id));
      setShowVerifyConfirm(false);
      setSelectedUser(null);
      fetchUsersData();
    } catch (error) {
      console.error('Verification failed:', error);
    }
  }, [dispatch, selectedUser, fetchUsersData]);

  const handleCloseDelete = useCallback(() => {
    setShowDeleteConfirm(false);
    setSelectedUser(null);
  }, []);

  const handleCloseStatus = useCallback(() => {
    setShowStatusConfirm(false);
    setSelectedUser(null);
  }, []);

  const handleClosePassword = useCallback(() => {
    setShowPasswordConfirm(false);
    setSelectedUser(null);
  }, []);

  const handleCloseVerify = useCallback(() => {
    setShowVerifyConfirm(false);
    setSelectedUser(null);
  }, []);

  const handleClearError = useCallback(() => {
    dispatch(clearUserError());
  }, [dispatch]);

  const handleClearSuccess = useCallback(() => {
    dispatch(clearUserSuccess());
  }, [dispatch]);

  const handleCloseTemporaryPassword = useCallback(() => {
    setTemporaryPassword(null);
  }, []);

  const getStatusBadge = (user) => {
    if (!user.isActive) {
      return <span style={styles.statusBadgeInactive}>Inactive</span>;
    }
    if (!user.isVerified) {
      return <span style={styles.statusBadgeUnverified}>Unverified</span>;
    }
    return <span style={styles.statusBadgeActive}>Active</span>;
  };

  if (loading && !users.length) {
    return <LoadingSpinner fullScreen />;
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>Gestion des Utilisateurs</h1>
        <Link
          to="/admin/users/form"
          style={styles.createButton}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = styles.createButtonHover.backgroundColor;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = styles.createButton.backgroundColor;
          }}
        >
          Ajouter un nouvel utilisateur
        </Link>
      </div>

      {error && (
        <Alert
          type="error"
          message={error}
          onClose={handleClearError}
        />
      )}

      {success && (
        <Alert
          type="success"
          message={success}
          onClose={handleClearSuccess}
        />
      )}

      {temporaryPassword && (
        <Alert
          type="info"
          message={`Mot de passe temporaire: ${temporaryPassword}`}
          onClose={handleCloseTemporaryPassword}
          autoClose={false}
        />
      )}

      <div style={styles.searchContainer}>
        <SearchBar 
          onSearch={handleSearch} 
          placeholder="Rechercher les utilisateurs par nom, email ou ID employé..." 
          fullWidth
        />
      </div>

      <div style={styles.filtersContainer}>
        <UserFilters filters={filters} onFilterChange={handleFilterChange} />
      </div>

      <div style={styles.tableContainer}>
        <table style={styles.table}>
          <thead style={styles.tableHeader}>
            <tr>
              <th style={styles.tableHeaderCell}>Utilisateur</th>
              <th style={styles.tableHeaderCell}>Contact</th>
              <th style={styles.tableHeaderCell}>épartement / Équipe</th>
              <th style={styles.tableHeaderCell}>Statut</th>
              <th style={styles.tableHeaderCell}>Rôle</th>
              <th style={styles.tableHeaderCellRight}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => (
              <tr 
                key={user.id} 
                style={styles.tableRow}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = styles.tableRowHover.backgroundColor;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                <td style={styles.tableCell}>
                  <div style={styles.userContainer}>
                    <div style={styles.avatar}>
                      {user.avatar ? (
                        <img
                          style={styles.avatarImage}
                          src={user.avatar.url}
                          alt=""
                        />
                      ) : (
                        <span style={styles.avatarFallback}>
                          {user.firstName[0]}
                          {user.lastName[0]}
                        </span>
                      )}
                    </div>
                    <div style={styles.userInfo}>
                      <div style={styles.userName}>
                        {user.displayName || `${user.firstName} ${user.lastName}`}
                      </div>
                      <div style={styles.userEmail}>{user.email}</div>
                      {user.employeeId && (
                        <div style={styles.userEmployeeId}>
                          ID: {user.employeeId}
                        </div>
                      )}
                    </div>
                  </div>
                </td>
                <td style={styles.tableCell}>
                  <div style={styles.jobTitle}>{user.jobTitle || '—'}</div>
                  <div style={styles.phoneNumber}>{user.phoneNumber || '—'}</div>
                </td>
                <td style={styles.tableCell}>
                  <div style={styles.department}>{user.department || '—'}</div>
                  <div style={styles.team}>{user.team || '—'}</div>
                </td>
                <td style={styles.tableCell}>
                  {getStatusBadge(user)}
                </td>
                <td style={styles.tableCell}>
                  <div style={styles.rolesContainer}>
                    {user.userRoles?.slice(0, 2).map((ur) => (
                      <span
                        key={ur.role.id}
                        style={styles.roleBadge}
                      >
                        {ur.role.name}
                      </span>
                    ))}
                    {user.userRoles?.length > 2 && (
                      <span style={styles.roleBadgeMore}>
                        +{user.userRoles.length - 2}
                      </span>
                    )}
                  </div>
                </td>
                <td style={styles.tableCellRight}>
                  <div style={styles.actionsContainer}>
                    <Link
                      to={`/admin/users/details/${user.id}`}
                      style={{
                        ...styles.actionButton,
                        ...styles.viewButton,
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.color = styles.viewButtonHover.color;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.color = styles.viewButton.color;
                      }}
                      title="View Details"
                    >
                      <svg style={styles.icon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    </Link>

                    <Link
                      to={`/admin/users/${user.id}/edit`}
                      style={{
                        ...styles.actionButton,
                        ...styles.editButton,
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.color = styles.editButtonHover.color;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.color = styles.editButton.color;
                      }}
                      title="Edit User"
                    >
                      <svg style={styles.icon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </Link>

                    {!user.isVerified && (
                      <button
                        onClick={() => handleVerify(user)}
                        style={{
                          ...styles.actionButton,
                          ...styles.verifyButton,
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.color = styles.verifyButtonHover.color;
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.color = styles.verifyButton.color;
                        }}
                        title="Verify User"
                      >
                        <svg style={styles.icon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </button>
                    )}

                    <button
                      onClick={() => handlePasswordReset(user)}
                      style={{
                        ...styles.actionButton,
                        ...styles.passwordButton,
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.color = styles.passwordButtonHover.color;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.color = styles.passwordButton.color;
                      }}
                      title="Reset Password"
                    >
                      <svg style={styles.icon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                      </svg>
                    </button>

                    <button
                      onClick={() => handleStatusToggle(user)}
                      style={{
                        ...styles.actionButton,
                        ...(user.isActive ? styles.statusButtonActive : styles.statusButtonInactive),
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.color = user.isActive 
                          ? styles.statusButtonHoverActive.color 
                          : styles.statusButtonHoverInactive.color;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.color = user.isActive 
                          ? styles.statusButtonActive.color 
                          : styles.statusButtonInactive.color;
                      }}
                      title={user.isActive ? 'Deactivate User' : 'Activate User'}
                    >
                      {user.isActive ? (
                        <svg style={styles.icon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                        </svg>
                      ) : (
                        <svg style={styles.icon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      )}
                    </button>

                    <button
                      onClick={() => handleDeleteClick(user)}
                      disabled={user.accessLevel === 'SUPER_ADMIN'}
                      style={{
                        ...styles.actionButton,
                        ...styles.deleteButton,
                        ...(user.accessLevel === 'SUPER_ADMIN' ? styles.actionButtonDisabled : {}),
                      }}
                      onMouseEnter={(e) => {
                        if (user.accessLevel !== 'SUPER_ADMIN') {
                          e.currentTarget.style.color = styles.deleteButtonHover.color;
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (user.accessLevel !== 'SUPER_ADMIN') {
                          e.currentTarget.style.color = styles.deleteButton.color;
                        }
                      }}
                      title={user.accessLevel === 'SUPER_ADMIN' ? 'Cannot delete super admin' : 'Delete User'}
                    >
                      <svg style={styles.icon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {users.length === 0 && !loading && (
          <div style={styles.emptyState}>
            Aucun utilisateur trouvé. Essayez d'ajuster vos filtres ou votre recherche.
          </div>
        )}

        <Pagination
          currentPage={pagination.page}
          totalPages={pagination.totalPages}
          onPageChange={handlePageChange}
        />
      </div>

      {/* Confirm Dialogs */}
      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={handleCloseDelete}
        onConfirm={handleDeleteConfirm}
        title="Supprimer l'utilisateur"
        message={`Etes-vous sûr de vouloir supprimer ${selectedUser?.displayName || selectedUser?.firstName}? Cette action ne peut pas être annulée.`}
        confirmText="Supprimer"
        cancelText="Annuler"
        type="danger"
      />

      <ConfirmDialog
        isOpen={showStatusConfirm}
        onClose={handleCloseStatus}
        onConfirm={handleStatusConfirm}
        title={`${selectedUser?.isActive ? 'Désactiver' : 'Activer'} l'utilisateur`}
        message={`Etes-vous sûr de vouloir ${selectedUser?.isActive ? 'désactiver' : 'activer'} ${selectedUser?.displayName || selectedUser?.firstName}?`}
        confirmText={selectedUser?.isActive ? 'Désactiver' : 'Activer'}
        cancelText="Annuler"
        type={selectedUser?.isActive ? 'warning' : 'success'}
      />

      <ConfirmDialog
        isOpen={showPasswordConfirm}
        onClose={handleClosePassword}
        onConfirm={handlePasswordConfirm}
        title="Reset Password"
        message={`Etes-vous sûr de vouloir réinitialiser le mot de passe pour ${selectedUser?.displayName || selectedUser?.firstName}? Un mot de passe temporaire sera généré.`}
        confirmText="Réinitialiser"
        cancelText="Annuler"
        type="warning"
      />

      <ConfirmDialog
        isOpen={showVerifyConfirm}
        onClose={handleCloseVerify}
        onConfirm={handleVerifyConfirm}
        title="Vérifier l'utilisateur"
        message={`Etes-vous sûr de vouloir vérifier ${selectedUser?.displayName || selectedUser?.firstName}?`}
        confirmText="Vérifier"
        cancelText="Annuler"
        type="info"
      />
    </div>
  );
};

export default UserList;